import * as Yup from "yup"
import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { notifications } from "@mantine/notifications"
import { useForm, yupResolver } from "@mantine/form"
import {
  TextInput,
  Textarea,
  createStyles,
  LoadingOverlay,
  rem,
  Button,
  Box,
  Group,
  Stack,
  Title, Alert,
  SegmentedControl,
  Text,
  Switch,
} from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { IconAlertCircle } from "@tabler/icons-react"

import { observer } from "mobx-react-lite"
import { PATHS } from "../../shared/lib/paths"
import { type ScheduleEvent, type ScheduleEventRequest } from "../../shared/types/scheduleEvent"
import { eventLevels } from "../../shared/lib/constants"
import { InstructorSelector } from "../instructor"
import { TextEditor } from "../../entities/textEditor"
import { DraftScheduleEventStore } from "../../app/store"
import { ScheduleFillFromDraftModal } from "./ScheduleFillFromDraftModal"
import { ScheduleEventStore } from "../../app/store"

// ----------------------------------------------------------------------

const useStyles = createStyles((theme) => ({
  header: {
    paddingBottom: theme.spacing.md,
    marginBottom: `calc(${theme.spacing.md} * 1.5)`,
    borderBottom: `${rem(1)} solid ${theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,

    [theme.fn.smallerThan("sm")]: {
      borderBottom: 0,
      paddingBottom: 0,
      marginBottom: theme.spacing.xl,
    },
  },
}))

const schema = Yup.object().shape({
  title: Yup.string().required("Заголовок мероприятия обязателен"),
  date: Yup.date().required("Выберите планируемую дату"),
  level: Yup.string().oneOf(eventLevels).default("based").nullable().notRequired(),
  isOnline: Yup.boolean().required(),
  instructor: Yup.string().required("Выберите преподавателя"),
  profession: Yup.object({
    description: Yup.string().required("Оставьте краткое описание курса"),
    duration: Yup.string().required("Укажите предполагаемую длительность обучения"),
  }).nullable().notRequired(),
  program: Yup.string().required("Предоставьте программу обучения"),
})

// ----------------------------------------------------------------------

export const ScheduleEdit = observer(function ScheduleEdit() {
  const { classes } = useStyles()
  const navigate = useNavigate()

  const [ draft, setDraft ] = useState<ScheduleEvent | null>(null)

  const [ initialProgram, setInitialProgram ] = useState<string>("")
  const { scheduleEventId } = useParams<{ scheduleEventId?: string }>()
  const {
    createScheduleEvent,
    updateScheduleEvent,
    loadScheduleEvent,
    error,
    isLoading,
  } = ScheduleEventStore

  const form = useForm<ScheduleEventRequest>({
    validateInputOnChange: true,
    validate: yupResolver(schema),
    initialValues: {
      title: "",
      date: new Date(),
      level: "based",
      isOnline: true,
      instructor: undefined,
      profession: null,
      program: "",
    },
  })

  function setData(data: ScheduleEvent) {
    const { _id, createdAt, updatedAt, ...dto } = data

    setInitialProgram(dto.program)
    form.setValues({ ...dto, instructor: dto.instructor?._id ?? "", date: new Date(dto.date) })
    form.resetDirty({ ...dto, instructor: dto.instructor?._id ?? "", date: new Date(dto.date) })
  }

  useEffect(() => {
    if (scheduleEventId) {
      void loadScheduleEvent(scheduleEventId).then((scheduleEvent) => {
        if (scheduleEvent) {
          setData(scheduleEvent)
        }
      })
    }

    return () => {
      form.reset()
    }
  }, [ scheduleEventId ])

  useEffect(() => {
    if (draft) {
      setData(draft)
      setDraft(null)
    }
  }, [ draft ])

  const onSubmit = (values: typeof form.values) => {
    if (scheduleEventId) {
      void updateScheduleEvent(scheduleEventId, values).then(success => {
        if (success) {
          navigate(PATHS.DASHBOARD.SCHEDULE.ROOT)
        }
      })
    } else {
      void createScheduleEvent(values).then(success => {
        if (success) {
          navigate(PATHS.DASHBOARD.SCHEDULE.ROOT)
        }
      })
    }
  }

  function saveDraft() {
    void DraftScheduleEventStore.saveDraft(form.values)
      .then((success) => {
        if (success) {
          return notifications.show({
            message: "Шаблон сохранен",
            color: "green",
          })
        }

        notifications.show({
          title: "Ошибка сохранения шаблона",
          message: DraftScheduleEventStore.error,
          color: "red",
        })
      })
  }

  return (
    <Box w="100%" pos="relative">
      <LoadingOverlay visible={isLoading} overlayBlur={2}/>

      <Group spacing="sm" position="apart" mih={50} className={classes.header}>
        <Title order={1} size={24}>
          {scheduleEventId ? "Редактирование события" : "Планирование события"}
        </Title>

        <Group spacing="sm">
          <Button
            disabled={!form.isValid()}
            loading={DraftScheduleEventStore.isLoading}
            compact
            variant="light"
            onClick={saveDraft}
          >
            Сохранить шаблон
          </Button>
          <ScheduleFillFromDraftModal setDraft={setDraft}/>
        </Group>
      </Group>

      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack spacing="xl">
          <TextInput label="Заголовок мероприятия" placeholder="Нутрициология" {...form.getInputProps("title")} />

          <DatePickerInput
            label="Планируемая дата"
            placeholder="22 сентября"
            valueFormat="DD MMMM YYYY"
            {...form.getInputProps("date")}
          />

          {!form.values.profession && <Stack spacing={0}>
            <Text size="sm">
              Уровень
            </Text>
            <SegmentedControl
              {...form.getInputProps("level")}
              data={[
                { label: "Базовый", value: "based" },
                { label: "Продвинутый", value: "advanced" },
              ]}
            />
          </Stack>}

          <InstructorSelector {...form.getInputProps("instructor")}/>

          <Switch label="Онлайн формат" {...form.getInputProps("isOnline", { type: "checkbox" })} />
          <Switch
            label="Профессия"
            checked={!!form.values.profession}
            onChange={(e) => {
              if (e.target.checked) {
                form.setFieldValue("level", null)
                return form.setFieldValue("profession", { description: "", duration: "" })
              }

              form.setFieldValue("profession", null)
              form.setFieldValue("level", "based")
            }}
          />

          {form.values.profession && (
            <>
              <Textarea
                label="Краткое описание профессии"
                {...form.getInputProps("profession.description")}
                minRows={2}
                autosize
              />
              <TextInput label="Длительность" placeholder="6 месяцев" {...form.getInputProps("profession.duration")} />
            </>
          )}

          <Stack spacing={0}>
            <Text size="sm">
              Программа обучения
            </Text>
            <TextEditor
              initialValue={initialProgram}
              placeholder="Опишите здесь этапы обучения"
              {...form.getInputProps("program")} />
          </Stack>
        </Stack>

        {error && (
          <Alert mt="xl" icon={<IconAlertCircle size="1rem"/>} title="Произошла ошибка" color="red">
            {error}
          </Alert>
        )}

        <Group position="center" my="xl">
          <Button disabled={!form.isDirty() || !form.isValid()} type="submit">
            {scheduleEventId ? "Обновить" : "Создать"}
          </Button>
          <Button disabled={isLoading} variant="light" color="gray" component={Link} to={PATHS.DASHBOARD.SCHEDULE.ROOT}>
            Назад
          </Button>
        </Group>
      </form>
    </Box>
  )
})
